import { useState, useEffect } from 'react';
import { Icon, Button, Input } from '@/components/atoms';
import { DiscountList } from '@/components/organisms/Discount/DiscountList';
import { DiscountForm } from '@/components/organisms/Discount/DiscountForm';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  useDiscountsList,
  useCreateDiscountMutation,
  useUpdateDiscountMutation,
  useDeleteDiscountMutation,
  useToggleDiscountMutation,
} from '@/hooks/useErpApi';
import {
  setDiscounts,
  addDiscount,
  updateDiscount as updateDiscountAction,
  removeDiscount,
  toggleDiscountActive,
  setFilters,
  clearFilters,
} from '@/features/discounts';
import {
  selectFilteredDiscounts,
  selectActiveDiscounts,
  selectExpiringDiscounts,
  selectDiscountsFilters,
} from '@/features/discounts/selectors';
import type { Discount, CreateDiscount } from '@/schemas/discounts';
import { DiscountType } from '@/schemas/discounts';

export default function DiscountsPage() {
  const dispatch = useAppDispatch();
  const { data: discountsData, isLoading, refetch } = useDiscountsList();
  const filteredDiscounts = useAppSelector(selectFilteredDiscounts);
  const activeDiscounts = useAppSelector(selectActiveDiscounts);
  const expiringDiscounts = useAppSelector(selectExpiringDiscounts);
  const filters = useAppSelector(selectDiscountsFilters);

  const createMutation = useCreateDiscountMutation();
  const updateMutation = useUpdateDiscountMutation();
  const deleteMutation = useDeleteDiscountMutation();
  const toggleMutation = useToggleDiscountMutation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<Discount | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (discountsData) {
      // Cast API response to local Discount type - status field type differs
      dispatch(setDiscounts(discountsData as unknown as Discount[]));
    }
  }, [discountsData, dispatch]);

  useEffect(() => {
    dispatch(setFilters({ search: searchQuery }));
  }, [searchQuery, dispatch]);

  const handleCreateDiscount = async (data: CreateDiscount) => {
    const response = await createMutation.mutate(data);
    if (response) {
      dispatch(addDiscount(response as unknown as Discount));
      await refetch();
    }
  };

  const handleUpdateDiscount = async (data: CreateDiscount) => {
    if (!editingDiscount) return;
    const response = await updateMutation.mutate({ id: editingDiscount.id, data });
    if (response) {
      dispatch(updateDiscountAction(response as unknown as Discount));
      await refetch();
    }
  };

  const handleDeleteDiscount = async (id: string) => {
    await deleteMutation.mutate(id);
    dispatch(removeDiscount(id));
    await refetch();
  };

  const handleToggleDiscount = async (id: string, isActive: boolean) => {
    await toggleMutation.mutate({ id, isActive });
    dispatch(toggleDiscountActive({ id, isActive }));
    await refetch();
  };

  const handleEdit = (discount: Discount) => {
    setEditingDiscount(discount);
    setIsFormOpen(true);
  };

  const handleDuplicate = (discount: Discount) => {
    setEditingDiscount({
      ...discount,
      id: '',
      code: `${discount.code}_COPY`,
      name: `${discount.name} (Copy)`,
      current_usage: 0,
    } as Discount);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingDiscount(undefined);
  };

  const handleFormSuccess = () => {
    refetch();
  };

  const getTotalRedemptions = (): number => {
    return discountsData?.reduce((sum, d) => sum + d.current_usage, 0) || 0;
  };

  const getMostUsedDiscount = (): Discount | null => {
    if (!discountsData || discountsData.length === 0) return null;
    return discountsData.reduce((max, d) =>
      d.current_usage > max.current_usage ? d : max
    ) as unknown as Discount;
  };

  const discountTypeOptions = [
    { value: '', label: 'All Types' },
    { value: DiscountType.FLAT, label: 'Flat Amount' },
    { value: DiscountType.PERCENTAGE, label: 'Percentage' },
    { value: DiscountType.BUY_X_GET_Y, label: 'Buy X Get Y' },
    { value: DiscountType.SEASONAL, label: 'Seasonal' },
    { value: DiscountType.BULK, label: 'Bulk Purchase' },
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'expired', label: 'Expired' },
    { value: 'usage_limit_reached', label: 'Limit Reached' },
    { value: 'scheduled', label: 'Scheduled' },
  ];

  const validityOptions = [
    { value: '', label: 'All Periods' },
    { value: 'current', label: 'Current' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'expired', label: 'Expired' },
  ];

  const mostUsed = getMostUsedDiscount();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 flex items-center gap-3">
            <Icon name="Percent" size={32} className="text-primary-600" />
            Discount Management
          </h1>
          <p className="text-neutral-600 mt-1">
            Create and manage promotional discounts to boost sales
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setIsFormOpen(true)}
          className="flex items-center gap-2"
        >
          <Icon name="Plus" size={20} />
          Create Discount
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-success-50 to-success-100 rounded-lg p-6 border border-success-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-success-700 text-sm font-medium">Active Discounts</p>
              <p className="text-3xl font-bold text-success-900 mt-2">{activeDiscounts.length}</p>
            </div>
            <div className="bg-success-200 p-3 rounded-lg">
              <Icon name="CircleCheck" size={24} className="text-success-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-6 border border-primary-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-700 text-sm font-medium">Total Redemptions</p>
              <p className="text-3xl font-bold text-primary-900 mt-2">{getTotalRedemptions()}</p>
            </div>
            <div className="bg-primary-200 p-3 rounded-lg">
              <Icon name="TrendingUp" size={24} className="text-primary-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-warning-50 to-warning-100 rounded-lg p-6 border border-warning-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warning-700 text-sm font-medium">Expiring Soon</p>
              <p className="text-3xl font-bold text-warning-900 mt-2">{expiringDiscounts.length}</p>
              <p className="text-xs text-warning-600 mt-1">Within 7 days</p>
            </div>
            <div className="bg-warning-200 p-3 rounded-lg">
              <Icon name="Clock" size={24} className="text-warning-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-info-50 to-info-100 rounded-lg p-6 border border-info-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-info-700 text-sm font-medium">Most Used</p>
              {mostUsed ? (
                <>
                  <p className="text-lg font-bold text-info-900 mt-2 truncate">{mostUsed.code}</p>
                  <p className="text-xs text-info-600 mt-1">{mostUsed.current_usage} uses</p>
                </>
              ) : (
                <p className="text-sm text-info-600 mt-2">No data yet</p>
              )}
            </div>
            <div className="bg-info-200 p-3 rounded-lg">
              <Icon name="Award" size={24} className="text-info-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Icon
                name="Search"
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400"
              />
              <Input
                placeholder="Search by code or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <select
            value={filters.type || ''}
            onChange={(e) =>
              dispatch(setFilters({ type: (e.target.value as DiscountType) || undefined }))
            }
            className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {discountTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={filters.status || ''}
            onChange={(e) =>
              dispatch(
                setFilters({
                  status:
                    (e.target.value as
                      | 'active'
                      | 'inactive'
                      | 'expired'
                      | 'usage_limit_reached'
                      | 'scheduled') || undefined,
                })
              )
            }
            className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={filters.validity || ''}
            onChange={(e) =>
              dispatch(
                setFilters({
                  validity: (e.target.value as 'current' | 'upcoming' | 'expired') || undefined,
                })
              )
            }
            className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {validityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {(filters.type || filters.status || filters.validity || searchQuery) && (
            <Button
              variant="ghost"
              onClick={() => {
                dispatch(clearFilters());
                setSearchQuery('');
              }}
              className="flex items-center gap-2"
            >
              <Icon name="X" size={16} />
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Discount List */}
      <DiscountList
        discounts={filteredDiscounts}
        onEdit={handleEdit}
        onDelete={handleDeleteDiscount}
        onToggle={handleToggleDiscount}
        onDuplicate={handleDuplicate}
        isLoading={isLoading}
      />

      {/* Discount Form Modal */}
      <DiscountForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        existingDiscount={editingDiscount}
        onSuccess={handleFormSuccess}
        onSubmit={editingDiscount ? handleUpdateDiscount : handleCreateDiscount}
      />
    </div>
  );
}
